import { MessageCircle, Mail, Check } from "lucide-react";

const AdvertiseWithUs = () => {
  const packages = [
    {
      duration: "5 Days",
      price: "₹500",
      description: "Perfect for short-term promotions and announcements",
      features: ["Display across all platforms", "WhatsApp + Email support", "Content moderation included"],
    },
    {
      duration: "10 Days",
      price: "₹1,000",
      description: "Extended visibility for maximum reach and engagement",
      features: ["Display across all platforms", "Priority support", "Content moderation included", "Analytics report"],
      featured: true,
    },
  ];

  return (
    <section id="advertise" className="py-20 sm:py-28 bg-gradient-to-b from-slate-50 to-white">
      <div className="section-container">
        <h2 className="section-title">Advertise with Us</h2>
        <p className="section-subtitle">
          Reach 2000+ residents with your message. Simple, affordable, and effective.
        </p>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className={`relative rounded-lg p-8 transition-all duration-300 ${
                pkg.featured
                  ? "bg-gradient-to-br from-blue-600 to-blue-800 text-white shadow-xl scale-105 md:scale-100"
                  : "bg-white border-2 border-slate-200 hover:border-blue-300 hover:shadow-lg"
              }`}
            >
              {pkg.featured && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-amber-400 text-slate-900 px-4 py-1 rounded-full text-sm font-bold">
                  Most Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className={`text-3xl font-bold mb-2 ${pkg.featured ? "text-white" : "text-slate-900"}`}>
                  {pkg.duration}
                </h3>
                <p className={`text-4xl font-bold mb-3 ${pkg.featured ? "text-white" : "text-blue-600"}`}>
                  {pkg.price}
                </p>
                <p className={`text-sm ${pkg.featured ? "text-blue-100" : "text-slate-600"}`}>
                  {pkg.description}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${pkg.featured ? "text-blue-200" : "text-green-600"}`} />
                    <span className={pkg.featured ? "text-blue-50" : "text-slate-700"}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-white border-2 border-blue-100 rounded-lg p-8 mb-16">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">
                1
              </div>
              <p className="font-semibold text-slate-900 mb-2">Choose Package</p>
              <p className="text-sm text-slate-600">Select 5-day or 10-day advertising plan</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">
                2
              </div>
              <p className="font-semibold text-slate-900 mb-2">Scan & Pay</p>
              <p className="text-sm text-slate-600">Scan the QR code and make payment via UPI/Card</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">
                3
              </div>
              <p className="font-semibold text-slate-900 mb-2">Share Proof</p>
              <p className="text-sm text-slate-600">Send payment screenshot via WhatsApp</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">
                4
              </div>
              <p className="font-semibold text-slate-900 mb-2">Go Live</p>
              <p className="text-sm text-slate-600">Your content displayed across all platforms</p>
            </div>
          </div>
        </div>

        {/* Payment QR Code */}
        <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-lg p-8 mb-16">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Scan & Pay</h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex-shrink-0">
              <img
                src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/bLNWmwwCeXUEoeDD.jpg"
                alt="Payment QR Code"
                className="w-64 h-64 object-contain rounded-lg shadow-lg"
              />
            </div>
            <div className="flex-1">
              <p className="text-slate-700 mb-4">
                Scan the QR code to make your payment securely via UPI, Cards, or Digital Rupees. 
                After payment, share the screenshot with us along with your advertising content.
              </p>
              <div className="space-y-2 text-sm text-slate-600">
                <p>✓ Secure payment via PayZapp</p>
                <p>✓ Instant confirmation</p>
                <p>✓ Multiple payment options</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* WhatsApp */}
          <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg p-8 hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-4">
              <MessageCircle className="w-8 h-8 text-green-600" />
              <h4 className="text-xl font-bold text-slate-900">WhatsApp Us</h4>
            </div>
            <p className="text-slate-600 mb-4">
              Share your payment screenshot and advertising content via WhatsApp. Our team will review and activate your ads within 24 hours.
            </p>
            <a
              href="https://wa.me/918838907864"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <p className="text-sm text-slate-600 mt-3">+91 88389 07864</p>
          </div>

          {/* Email */}
          <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-8 hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
              <h4 className="text-xl font-bold text-slate-900">Email Us</h4>
            </div>
            <p className="text-slate-600 mb-4">
              Send your payment proof and advertising content via email. Include your name, contact number, and preferred dates.
            </p>
            <a
              href="mailto:office.psaoa@gmail.com"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5" />
              Send Email
            </a>
            <p className="text-sm text-slate-600 mt-3">office.psaoa@gmail.com</p>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 bg-white rounded-lg p-8 border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-8">Frequently Asked Questions</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-bold text-slate-900 mb-2">What content can I advertise?</h4>
              <p className="text-slate-600">
                You can advertise products, services, events, or announcements relevant to our community. Content must be appropriate and non-offensive.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-2">Where will my ads be displayed?</h4>
              <p className="text-slate-600">
                Your content will be displayed across all community platforms including the website, WhatsApp groups, notice boards, and community app.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-2">How long does activation take?</h4>
              <p className="text-slate-600">
                After we receive your payment proof and content, activation typically takes 24 hours. You'll receive confirmation via WhatsApp or email.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-2">Can I extend my advertisement?</h4>
              <p className="text-slate-600">
                Yes! You can extend your advertisement by making an additional payment. Contact us via WhatsApp or email for extension requests.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvertiseWithUs;
